import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pathname } from 'Routes';
import useSWR from 'swr';
import {
  Box,
  Card,
  Stack,
  Typography,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Snackbar } from 'Components';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import AlterImage from 'Assets/alter-image.png';
import {
  EmailEditModal,
  PhoneNumberEditModal,
  PasswordEditModal,
} from 'Components/edits';
import { WarningPopup } from 'Components/WarningPopup';
import LogoutButton from 'Components/LogoutButton';
import { AVATAR_COLLECTION, presetAvatarToDataUrl } from 'Utils/avatarCollection';
import { NAME_MAX_LENGTH, NAME_LIMIT_MESSAGE, nameLimitHandlers } from 'Utils/nameLimit';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

// A settings row. Inline-editable fields (name) swap the value for the supplied
// `editor` with Save / Cancel. Verification-gated fields (email / phone /
// password) can't change without a code, so their pencil opens the existing
// secure flow via `onEdit` — no more per-row "Change" text button either way.
const FieldRow = ({
  icon: Icon,
  label,
  value,
  placeholder,
  editing,
  editor,
  saving,
  onEdit,
  onSave,
  onCancel,
}) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={{ xs: 1, sm: 2 }}
    sx={{
      alignItems: { sm: 'center' },
      justifyContent: 'space-between',
      py: { xs: 1, md: 1 },
    }}
  >
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
          {label}
        </Typography>
        {editing ? (
          <Box sx={{ mt: 0.5 }}>{editor}</Box>
        ) : (
          <Typography
            sx={{
              fontWeight: 600,
              color: value ? 'text.primary' : 'text.secondary',
              wordBreak: 'break-word',
              fontStyle: value ? 'normal' : 'italic',
              mt: 0.25,
            }}
          >
            {value || placeholder || 'Not set'}
          </Typography>
        )}
      </Box>
    </Stack>
    {editing ? (
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={saving}
          sx={{ borderRadius: 2, textTransform: 'none', minWidth: 74 }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Save'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          disabled={saving}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Cancel
        </Button>
      </Stack>
    ) : (
      <Tooltip title={`Edit ${label}`}>
        <IconButton
          onClick={onEdit}
          size="small"
          aria-label={`Edit ${label}`}
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'flex-end', sm: 'center' },
            color: 'text.secondary',
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <EditRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Stack>
);

export const AccountSettingsV2 = () => {
  const {
    userDetails,
    setUserDetails,
    langText,
    autoSignIn,
    warningTitle,
    warningMessage,
  } = useContext(AppContext);

  const [avatar, setAvatar] = useState(undefined);
  const [avatarUploadError, setAvatarUploadErrorStatus] = useState(false);
  const [emailEditModalIsVisible, setEmailEditModalVisibility] = useState(false);
  const [phoneNumberEditModalIsVisible, setPhoneNumberEditModalVisibility] = useState(false);
  const [passwordEditModalIsVisible, setPasswordEditModalVisibility] = useState(false);
  const [warningPopup, setWarningPopup] = useState(false);
  const [snackbar, setSnackbar] = useState(defaultSnackbar);

  const nameLimitProps = nameLimitHandlers(() => {
    setSnackbar({ isVisible: true, type: 'error', message: NAME_LIMIT_MESSAGE });
  });
  // Inline name editing (the one field that can be changed without a code).
  const [editingName, setEditingName] = useState(false);
  const [firstDraft, setFirstDraft] = useState('');
  const [lastDraft, setLastDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const accountText = langText?.settings?.account;

  const revealEmailEditModal = () => {
    if (userDetails?.isLoginUsingSocial) {
      setWarningPopup(true);
    } else {
      setEmailEditModalVisibility(true);
    }
  };
  const hideEmailEditModal = () => setEmailEditModalVisibility(false);
  const revealPhoneNumberEditModal = () => navigate(Pathname.authentication.mobileVerification);
  const hidePhoneNumberEditModal = () => setPhoneNumberEditModalVisibility(false);
  const revealPasswordEditModal = () => setPasswordEditModalVisibility(true);
  const hidePasswordEditModal = () => setPasswordEditModalVisibility(false);

  const startEditName = () => {
    setFirstDraft(userDetails?.firstname || '');
    setLastDraft(userDetails?.lastname || '');
    setEditingName(true);
  };
  const cancelEditName = () => setEditingName(false);
  const saveName = async () => {
    const first = firstDraft.trim();
    const last = lastDraft.trim();
    if (!first && !last) {
      setSnackbar({ isVisible: true, type: 'error', message: 'Please enter a name' });
      return;
    }
    setSavingName(true);
    try {
      const res = await dataFetcher(endpoints.updateMyProfile, { firstname: first, lastname: last });
      if (res?.success) {
        setUserDetails({ ...userDetails, firstname: first, lastname: last });
        setSnackbar({ isVisible: true, type: 'success', message: 'Name updated successfully' });
        setEditingName(false);
      } else {
        setSnackbar({ isVisible: true, type: 'error', message: 'Could not update your name' });
      }
    } catch (e) {
      setSnackbar({ isVisible: true, type: 'error', message: 'Could not update your name' });
    } finally {
      setSavingName(false);
    }
  };

  const { isValidating: updatingAvatar, mutate: updateAvatar } = useSWR(
    [endpoints.updateProfileImge, avatar],
    {
      fetcher: (url, inputAvatar) => dataFetcher(url, { image: inputAvatar }),
      onSuccess: ({ success, image }) => {
        if (success) {
          setAvatarUploadErrorStatus(false);
          setSnackbar({ isVisible: true, type: 'success', message: 'Avatar was updated successfully' });
          setUserDetails({ ...userDetails, dpURL: image });
          autoSignIn();
        } else {
          setAvatarUploadErrorStatus(true);
          setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating your avatar' });
        }
      },
      onError: () => {
        setAvatarUploadErrorStatus(true);
        setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating your avatar' });
      },
    },
  );

  useEffect(() => {
    if (avatar) updateAvatar();
  }, [avatar, updateAvatar]);

  const handleAvatarChange = ({ target: { files } }) => {
    const reader = new FileReader();
    const tempAvatar = files[0];
    if (tempAvatar) reader.readAsDataURL(tempAvatar);
    reader.addEventListener('load', () => setAvatar(reader.result), false);
  };

  const fullName = (userDetails?.firstname?.trim() || userDetails?.lastname?.trim())
    ? `${userDetails?.firstname || ''} ${userDetails?.lastname || ''}`.trim()
    : '';

  // Suggested avatars are the same illustrated presets offered on the
  // onboarding "Choose Your Avatar" screen, applied through the same base64
  // upload flow as a file upload.
  const applyPresetAvatar = async (src) => {
    try {
      const dataUrl = await presetAvatarToDataUrl(src);
      setAvatar(dataUrl);
    } catch (e) {
      setSnackbar({ isVisible: true, type: 'error', message: 'Could not apply that avatar' });
    }
  };

  const initials = (() => {
    const f = userDetails?.firstname?.trim()?.[0] || '';
    const l = userDetails?.lastname?.trim()?.[0] || '';
    return (f + l).toUpperCase() || (userDetails?.email?.[0]?.toUpperCase() || 'U');
  })();

  return (
    <>
      <V2SettingsShell
        fitViewport
        title={accountText?.title || 'Account details'}
        description={accountText?.description || 'Choose how your friends see you on Projector'}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={{ xs: 3, lg: 2.5 }}
          sx={{
            alignItems: 'stretch',
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            my: 'auto',
          }}
        >
          <Card
            sx={{
              p: { xs: 2.5, md: 2.5 },
              width: { xs: '100%', lg: 300, xl: 340 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              spacing={1.5}
              sx={{
                alignItems: 'center',
                textAlign: 'center',
                flex: 1,
                // The card is as tall as the row allows and Card is
                // overflow:hidden, so this column must be able to shrink and
                // scroll — otherwise its last child (Log out) is clipped away.
                // Auto margins keep the group centred when there IS room;
                // justifyContent:center can't, because it puts the overflow
                // above the scroll origin where it can never be reached.
                minHeight: 0,
                overflowY: 'auto',
                '& > :first-of-type': { marginTop: 'auto' },
                '& > :last-of-type': { marginBottom: 'auto' },
              }}
            >
              <Box sx={{ position: 'relative', width: 96, height: 96 }}>
                {userDetails?.dpURL ? (
                  <Avatar
                    src={userDetails?.dpURL}
                    alt="profile"
                    imgProps={{
                      onError: (e) => { e.target.src = AlterImage; },
                    }}
                    sx={{
                      width: 96,
                      height: 96,
                      border: '3px solid',
                      borderColor: 'background.paper',
                      boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 96,
                      height: 96,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: 32,
                      fontWeight: 700,
                      boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
                    }}
                  >
                    {initials}
                  </Avatar>
                )}
                <input
                  accept="image/*"
                  disabled={updatingAvatar}
                  id="account-profile-pic-v2"
                  type="file"
                  onChange={handleAvatarChange}
                  style={{
                    position: 'absolute',
                    width: 0,
                    height: 0,
                    opacity: 0,
                    overflow: 'hidden',
                  }}
                />
                <IconButton
                  component="label"
                  htmlFor="account-profile-pic-v2"
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: '0 4px 12px rgba(15,23,42,0.18)',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  {updatingAvatar ? (
                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  ) : (
                    <CameraAltRoundedIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 0.75 }}>
                  Try a suggested avatar
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ justifyContent: 'center', flexWrap: 'wrap' }}
                >
                  {AVATAR_COLLECTION.slice(0, 5).map((presetAvatar, i) => (
                    <Box
                      key={presetAvatar}
                      component="button"
                      type="button"
                      onClick={() => applyPresetAvatar(presetAvatar)}
                      disabled={updatingAvatar}
                      aria-label={`Use suggested avatar ${i + 1}`}
                      sx={{
                        p: 0,
                        border: '2px solid transparent',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        background: 'none',
                        lineHeight: 0,
                        transition: 'transform 0.15s ease, border-color 0.15s ease',
                        '&:hover': { transform: 'translateY(-2px)', borderColor: 'primary.main' },
                        '&:disabled': { opacity: 0.5, cursor: 'default' },
                      }}
                    >
                      <img
                        src={presetAvatar}
                        width={40}
                        height={40}
                        alt=""
                        style={{ display: 'block', borderRadius: 10, objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {fullName || 'Add your name'}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', wordBreak: 'break-word' }}
                  >
                    {userDetails?.email}
                  </Typography>
                  {userDetails?.email && (
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  )}
                </Stack>
                {avatarUploadError && (
                  <Button
                    component="label"
                    htmlFor="account-profile-pic-v2"
                    size="small"
                    color="error"
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Retry upload
                  </Button>
                )}
              </Box>

              <LogoutButton />
            </Stack>
          </Card>

          <Card
            sx={{
              p: { xs: 2, md: 2.5 },
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* MUI's overline line-height is 2.66 — nearly 32px of box for a
                12px label. Tightened so the four rows below keep their room. */}
            <Box sx={{ px: { xs: 1, md: 1.5 }, pt: 0.5, pb: 0.5 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  letterSpacing: 0.6,
                  display: 'block',
                  lineHeight: 1.6,
                }}
              >
                Profile
              </Typography>
            </Box>
            <Box
              sx={{
                px: { xs: 1, md: 1.5 },
                // Fill the stretched card so the rows spread instead of
                // leaving a large gap under the last one — but never clip: if a
                // row grows (inline editing) let the group scroll inside the card.
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
              }}
            >
              <FieldRow
                icon={PersonRoundedIcon}
                label={accountText?.profile || 'Display name'}
                value={fullName}
                placeholder="Update profile name"
                editing={editingName}
                saving={savingName}
                onEdit={startEditName}
                onSave={saveName}
                onCancel={cancelEditName}
                editor={(
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      value={firstDraft}
                      onChange={(e) => setFirstDraft(e.target.value.slice(0, NAME_MAX_LENGTH))}
                      placeholder="First name"
                      size="small"
                      inputProps={{ maxLength: NAME_MAX_LENGTH }}
                      onPaste={nameLimitProps.onPaste}
                      autoFocus
                      disabled={savingName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveName();
                        if (e.key === 'Escape') cancelEditName();
                        nameLimitProps.onKeyDown(e);
                      }}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      value={lastDraft}
                      onChange={(e) => setLastDraft(e.target.value.slice(0, NAME_MAX_LENGTH))}
                      placeholder="Last name"
                      size="small"
                      inputProps={{ maxLength: NAME_MAX_LENGTH }}
                      onPaste={nameLimitProps.onPaste}
                      disabled={savingName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveName();
                        if (e.key === 'Escape') cancelEditName();
                        nameLimitProps.onKeyDown(e);
                      }}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }}
                    />
                  </Stack>
                )}
              />
              <Divider />
              <FieldRow
                icon={EmailRoundedIcon}
                label={accountText?.email || 'Email'}
                value={userDetails?.email}
                onEdit={revealEmailEditModal}
              />
              <Divider />
              <FieldRow
                icon={PhoneRoundedIcon}
                label={accountText?.phonenumber || 'Phone number'}
                value={userDetails?.mobile}
                placeholder="Add phone number"
                onEdit={revealPhoneNumberEditModal}
              />
              <Divider />
              <FieldRow
                icon={LockRoundedIcon}
                label={accountText?.password || 'Password'}
                value={!userDetails?.no_password ? '•••••••••••••' : ''}
                placeholder="Set a password"
                onEdit={revealPasswordEditModal}
              />
            </Box>
          </Card>
        </Stack>
      </V2SettingsShell>

      <EmailEditModal
        isVisible={emailEditModalIsVisible}
        onHide={hideEmailEditModal}
        subtitle="We have sent the reset code to your existing email."
        title={accountText?.updateEmail || 'Update Email'}
        resetCodeLabel={accountText?.enterResetCode || 'Enter reset code'}
        newEmailLabel={accountText?.enterNewEmail || 'Enter New Email'}
      />
      <PhoneNumberEditModal
        isVisible={phoneNumberEditModalIsVisible}
        onHide={hidePhoneNumberEditModal}
        title={accountText?.updatepPhoneNumber || 'Update Phone number'}
        phoneNumerLabel={accountText?.enterPhoneNumber || 'Enter Phone number'}
      />
      <PasswordEditModal
        isVisible={passwordEditModalIsVisible}
        subtitle="We have sent the reset code to your existing email."
        onHide={hidePasswordEditModal}
        title={accountText?.updatePassword || 'Update Password'}
        currentPasswordLabel="Enter reset code"
        newPasswordLabel={accountText?.enterNewPassword || 'Enter new password'}
        passwordRule={accountText?.passwordDescription || 'Use a minimum of 6 characters (case sensitive) with at lease one number and one special character.'}
      />
      <WarningPopup
        isVisible={warningPopup}
        onCancel={() => setWarningPopup(false)}
        onContinue={() => {
          setWarningPopup(false);
          setEmailEditModalVisibility(true);
        }}
        title={warningTitle}
        message={warningMessage}
        cancelText="Cancel"
        continueText="Continue"
      />
      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => setSnackbar(defaultSnackbar)}
        type={snackbar?.type}
      />
    </>
  );
};
